import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCTION_TICKET, ProductionTicketStatusValues, PRODUCTION_TICKET_STATUS_LABELS, } from '../types/production-ticket.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductionTicket (org/{orgId}/production-ticket).
 *
 * Top-level transactional collection (not a subcollection of the OP). No
 * lifecycle fields; the only lifecycle is the business `status`. Keeps the
 * Auditable block. Created only via generateTickets — never a free draft.
 */
export const productionTicketDescriptor = {
    name: PRODUCTION_TICKET,
    label: 'Talão de produção',
    domain: 'production',
    collectionPath: 'org/{orgId}/production-ticket',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Production ticket (talão) — planned slice of a production order (spec 0040 §2.5)',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Unique production ticket identifier',
        },
        {
            name: 'productionOrderId',
            label: 'Ordem de produção',
            type: 'string',
            required: true,
            description: 'Owning production order',
        },
        {
            name: 'sequence',
            example: 1,
            label: 'Sequência',
            type: 'number',
            required: true,
            description: 'Ordinal within the OP (1-based; continues across later generations)',
        },
        {
            name: 'ticketNumber',
            example: '1001-1',
            label: 'Número do talão',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.CODE,
            description: 'Snapshot at generation: {code}-{sequence}. Not rewritten if the OP is renamed.',
        },
        {
            name: 'productId',
            label: 'Produto',
            type: 'string',
            required: true,
            description: 'Denormalized from the OP header at generation (immutable)',
        },
        {
            name: 'variantId',
            label: 'Variante',
            type: 'string',
            required: false,
            description: 'Denormalized partial variant from the OP header (immutable)',
        },
        {
            name: 'quantity',
            example: 50,
            label: 'Quantidade',
            type: 'number',
            display: 'quantity',
            required: true,
            description: 'Planned slice of the OP quantity',
        },
        {
            name: 'machineId',
            label: 'Máquina',
            type: 'string',
            required: false,
            description: 'Set when generated by machine batch (informative)',
        },
        {
            name: 'status',
            example: 'open',
            label: 'Status',
            type: 'enum',
            required: true,
            enumValues: [...ProductionTicketStatusValues],
            enumLabels: PRODUCTION_TICKET_STATUS_LABELS,
            enumTones: {
                open: 'neutral',
                'in-progress': 'info',
                done: 'success',
                cancelled: 'destructive',
            },
            description: 'Ticket status (open → in-progress → done | cancelled)',
            defaultValue: 'open',
        },
        {
            name: 'notes',
            example: 'Primeira fatia',
            label: 'Observações',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NOTES,
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'productionOrderId',
            targetEntity: 'ProductionOrder',
            relationship: 'many-to-one',
            displayField: 'code',
            description: 'Ordem de produção',
        },
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Produto (denormalizado da OP)',
        },
        {
            fieldName: 'variantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Variante parcial (denormalizada da OP)',
        },
        {
            fieldName: 'machineId',
            targetEntity: 'Machine',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Máquina (geração por lote)',
        },
    ],
};
//# sourceMappingURL=production-ticket.descriptor.js.map